import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
// form
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
// @mui
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { Helmet } from "react-helmet-async";
import FormProvider, {
  RHFSelect,
  RHFTextField,
} from "../../components/hook-form";

import { useSettingsContext } from "../../components/settings";

import { useSnackbar } from "../../components/snackbar";

// TODO: redux
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";
import EditDialog from "../../components/edit-dialog/EditDialog";
import RHFDateTimeField from "../../components/hook-form/RHFDateTimeField";
import { Upload } from "../../components/upload";
import {
  createFanChoices,
  udpateFanChoice,
} from "../../redux/slices/fanchoice";
import { getGames } from "../../redux/slices/game";
import { getSeasons } from "../../redux/slices/season";
import { getVerifiedPlayers } from "../../redux/slices/user";
import VotedUserList from "./VotedUserList";
// ----------------------------------------------------------------------

FanChoiceForm.propTypes = {
  isEdit: PropTypes.bool,
  submitForm: PropTypes.func,
  setOpenEdit: PropTypes.func,
};

export default function FanChoiceForm({ isEdit = false, row, view = false }) {
  // TODO: declarations
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // TODO: fetch

  // TODO: fetch data from store

  const [gameOptions, setGameOptions] = useState();
  const [selectedOption, setSelectedOption] = useState(false);
  const [openUserList, setOpenUserList] = useState(false);

  // TODO: options of the question
  const [options, setOptions] = useState([
    {
      name: "",
      link: "",
      image: "",
    },
  ]);

  const seasons = useSelector((state) => state.season.seasons);
  const stadiumLoading = useSelector((state) => state.stadium.fetchLoading);
  const seasonLoading = useSelector((state) => state.season.fetchLoading);

  const teamLoading = useSelector((state) => state.team.fetchLoading);
  const isLoading = useSelector((state) => state.fanChoice.isLoading);
  const games = useSelector((state) => state.game.games);
  const fanChoiceResults = useSelector(
    (state) => state.fanVoice.fanChoiceResults
  );

  const Schema = Yup.object().shape({
    question: Yup.string().required("question is required"),
    season_id: Yup.string().required("Season is required"),
    from: Yup.string().required("Start date time is required"),
    to: Yup.string().required("End date time is required"),
  });

  const defaultValues = useMemo(
    () => ({
      season_id: row?.season_id || seasons?.[seasons?.length - 1]?.id || "",
      question: row?.question,
      from: row?.from,
      to: row?.to,
      game_id: gameOptions?.find(
        (game) => game?.match_no === row?.game?.match_no
      )?.id,
      options: row?.options?.map((option) => ({
        id: option?.id,
        name: option?.name || "",
        link: option?.link || "",
        image: option?.image || "",
      })) || [{ name: "", link: "", image: "" }],
      player: row?.player || 0,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row]
  );

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const { handleSubmit, reset, watch } = methods;

  useEffect(() => {
    if (isEdit && row) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, row]);

  useEffect(() => {
    dispatch(getSeasons({}));
  }, [dispatch]);

  // TODO: fetch the games according to season selected
  useEffect(() => {
    watch()?.season_id && dispatch(getGames({ season_id: watch()?.season_id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, watch()?.season_id]);

  // TODO: submit the blog
  const onSubmit = async (values) => {
    const formData = new FormData();
    options.forEach((opt, key) => {
      formData.append(`options[${key}][name]`, opt?.name);
      formData.append(`options[${key}][image]`, opt?.image);
      formData.append(`options[${key}][link]`, opt?.link);
    });
    formData.append("from", moment(values.from).format("YYYY-MM-DD HH:MM:SS"));
    formData.append("to", moment(values.to).format("YYYY-MM-DD HH:MM:SS"));
    formData.append("season_id", values.season_id);
    formData.append("question", values.question);
    formData.append("yesno", 0);

    dispatch(
      createFanChoices({
        data: formData,
        enqueueSnackbar,
        navigate,
      })
    );
  };

  // TODO: submit the fan choice
  const onUpdate = async (values) => {
    const formData = new FormData();

    options.forEach((opt, key) => {
      opt?.id && formData.append(`options[${key}][id]`, opt?.id);
      formData.append(`options[${key}][name]`, opt?.name);
      formData.append(`options[${key}][image]`, opt?.image);
      formData.append(`options[${key}][link]`, opt?.link);
    });
    formData.append("from", moment(values.from).format("YYYY-MM-DD HH:MM:SS"));
    formData.append("to", moment(values.to).format("YYYY-MM-DD HH:MM:SS"));
    formData.append("season_id", values.season_id);
    formData.append("question", values.question);
    formData.append("yesno", 0);

    dispatch(
      udpateFanChoice({
        data: formData,
        id: row?.id,
        enqueueSnackbar,
      })
    );
  };

  useEffect(() => {
    const gameData = games?.map((game) => ({
      label: `${game?.first_team?.name} vs ${game?.second_team?.name}`,
      id: game?.id,
      match_no: game?.match_number,
    }));

    setGameOptions(gameData);
  }, [games]);

  useEffect(() => {
    dispatch(getVerifiedPlayers({ limit: 500 }));
  }, [dispatch]);

  const handleDropSingleFile = useCallback((acceptedFiles, index) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      // Add preview property to newFile using Object.assign
      const fileWithPreview = Object.assign(newFile, {
        preview: URL.createObjectURL(newFile),
      });

      setOptions((prevOptions) => {
        // Make a copy of the options array
        const newOptions = [...prevOptions];

        // Update the object at the specified index
        if (index >= 0 && index < newOptions.length) {
          newOptions[index] = {
            ...newOptions[index], // Copy previous object properties
            image: fileWithPreview, // Use the modified newFile with preview
          };
        }

        return newOptions;
      });
    }
  }, []);

  const handleAddNewOption = () => {
    setOptions((prev) => [...prev, { name: "", image: "", link: "" }]);
  };

  const handleAddNameOption = (value, index) => {
    setOptions((prev) => {
      const newOptions = [...prev];
      // Update the object at the specified index
      if (index >= 0 && index < newOptions.length) {
        newOptions[index] = {
          ...newOptions[index], // Copy previous object properties
          name: value,
        };
      }

      return newOptions;
    });
  };

  const handleAddLinkOption = (value, index) => {
    setOptions((prev) => {
      const newOptions = [...prev];
      // Update the object at the specified index
      if (index >= 0 && index < newOptions.length) {
        newOptions[index] = {
          ...newOptions[index], // Copy previous object properties
          link: value,
        };
      }

      return newOptions;
    });
  };

  const handleDeleteOption = (index) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };
  useEffect(() => {
    if (row) {
      setOptions(defaultValues.options);
    }
  }, [row, defaultValues.options]);

  const groupedData = fanChoiceResults?.reduce((acc, current) => {
    const optionId = current.option.id;
    if (!acc[optionId]) {
      acc[optionId] = {
        option: current.option,
        users: [],
      };
    }
    acc[optionId].users.push(current.user);
    return acc;
  }, {});

  // TODO: user count = fanChoiceResults?.length

  const userCount = fanChoiceResults?.length;

  return (
    <>
      <Helmet>
        <title> {`NKL: ${view ? "View" : "Create"} Fan choice`}</title>
      </Helmet>

      {seasonLoading && teamLoading && stadiumLoading ? (
        <LinearProgress />
      ) : (
        <Container maxWidth={themeStretch ? false : "xl"}>
          <FormProvider
            methods={methods}
            onSubmit={isEdit ? handleSubmit(onUpdate) : handleSubmit(onSubmit)}
          >
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Box sx={{ p: 3, pb: 0 }}>
                    <RHFTextField
                      multiline
                      rows={3}
                      name="question"
                      label="Question *"
                      disabled={view}
                    />
                  </Box>
                  <Box
                    rowGap={3}
                    columnGap={2}
                    display="grid"
                    gridTemplateColumns={{
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(3, 1fr)",
                    }}
                    sx={{ p: 3 }}
                  >
                    <RHFSelect
                      name="season_id"
                      label="Select Season *"
                      disabled={view}
                    >
                      {seasons?.map((season) => {
                        return (
                          <MenuItem key={season?.id} value={season?.id}>
                            {season?.name}
                          </MenuItem>
                        );
                      })}
                    </RHFSelect>

                    <RHFDateTimeField label="Start date time" name="from" />
                    <RHFDateTimeField label="End date time" name="to" />
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ p: 3, pt: 0 }}>
                <Stack flexDirection={"row"} gap={5}>
                  <Typography variant="button">Options</Typography>

                  <Typography variant="button">
                    Total Voters- {userCount}
                  </Typography>
                </Stack>

                <Divider />

                <Stack flexDirection={"row"} flexWrap={"wrap"} gap={3} mt={3}>
                  {options?.map((opt, index) => (
                    <Box key={opt} flex={1} minWidth={300} maxWidth={400}>
                      <Stack gap={2}>
                        <TextField
                          value={opt?.name}
                          label="Option name *"
                          name="name"
                          onChange={(e) =>
                            handleAddNameOption(e.target.value, index)
                          }
                        />
                        <TextField
                          value={opt?.link}
                          label="Option video link (if any)"
                          onChange={(e) =>
                            handleAddLinkOption(e.target.value, index)
                          }
                        />

                        <span
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          (1mb) (jpeg, jpg, png)
                        </span>
                        <Box
                          sx={{
                            position: "relative",
                          }}
                        >
                          <Upload
                            disabled={view}
                            text="Drop or Select Photo/Thumbnail"
                            file={opt?.image}
                            name="image"
                            accept="image/*"
                            onDrop={(file) => handleDropSingleFile(file, index)}
                            maxSize={1}
                          />
                          {opt?.id && (
                            <Button
                              disabled={!groupedData?.[opt?.id]?.users?.length}
                              onClick={() => {
                                setSelectedOption(opt?.id);
                                setOpenUserList(true);
                              }}
                              // variant="contained"
                              sx={{
                                position: "absolute",
                                top: 0,
                                zIndex: 100,
                                backgroundColor: "black",
                                color: "white !important",
                              }}
                            >
                              Vote -{" "}
                              {userCount !== 0 ? (
                                <>
                                  {(
                                    ((groupedData?.[opt?.id]?.users?.length ||
                                      0) /
                                      userCount) *
                                    100
                                  ).toFixed(1)}{" "}
                                </>
                              ) : (
                                0
                              )}
                              %
                            </Button>
                          )}
                        </Box>

                        <Button
                          disabled={options.length === 1}
                          variant="outlined"
                          sx={{ width: "fit-content" }}
                          onClick={() => handleDeleteOption(index)}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                  <Button
                    disabled={
                      options?.slice(-1)[0]?.image === "" ||
                      options?.slice(-1)[0]?.name === ""
                    }
                    onClick={handleAddNewOption}
                    sx={{ height: "fit-content" }}
                  >
                    Add option
                  </Button>
                </Stack>
              </Box>
              {!view && (
                <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    {isEdit ? "Update Fan Choice" : "Create Fan Choice"}
                  </LoadingButton>
                </Stack>
              )}
            </Box>
          </FormProvider>
        </Container>
      )}

      {/* TODO: users list who did voting for particular option */}

      <EditDialog
        title={`Voters list for ${groupedData?.[selectedOption]?.option?.name}`}
        open={openUserList}
        showClose={true}
        onCloseButton={() => setOpenUserList(false)}
        children={<VotedUserList data={groupedData?.[selectedOption]?.users} />}
      />
    </>
  );
}
